import {add, all, bignumber, BigNumber, ConfigOptions, create, exp, floor, multiply, subtract} from "mathjs";
import {Unit} from "./unit/unit";
import {TemperatureUnitCelsius, TemperatureUnitFahrenheit} from "./unit/temperature-unit";
import {Temperature} from "./temperature";
import {Commodity, getCommodity} from "./commodity/commodity";
import {CorrectionFactors} from "./correction-factors";
import {OutOfLimitsError} from "./error/out-of-limits-error";
import {CommodityGroup} from "./commodity/commodity-group";
import {Density} from "./density";
import {Pressure} from "./pressure";
import {PressureUnitPsig} from "./unit/pressure-unit";
import {DensityUnitKgm3} from "./unit/density-unit";
import {NonConvergenceError} from "./error/non-convergence-error";

const config: ConfigOptions = {
  relTol: 1e-12,
  absTol: 1e-15,
  matrix: 'Matrix',
  number: 'number',
  precision: 300,
  predictable: false,
  randomSeed: null
}

export const apiMath = create(all, config)

export const DENSITY_OF_WATER_AT_BASE_KGM3 = bignumber("999.016")
export const TEMPERATURE_FAHRENHEIT_LIMITS = [bignumber("-58.0"), bignumber("302.0")]
export const PRESSURE_PSIG_LIMITS = [bignumber("0"), bignumber("1500.0")]

export const TEMPERATURE_SHIFT_VALUE_FAHRENHEIT = bignumber("0.01374979547")
export const TEMPERATURE_BASE_FAHRENHEIT = bignumber("60.0068749")

export const A_COEFFICIENTS = [
  bignumber("-0.148759"),
  bignumber("-0.267408"),
  bignumber("1.080760"),
  bignumber("1.269056"),
  bignumber("-4.089591"),
  bignumber("-1.871251"),
  bignumber("7.438081"),
  bignumber("-3.536296")
]

export const divide = (x: BigNumber, y: BigNumber) => {
  const divideResult = apiMath.divide(x, y) as BigNumber
  return apiMath.ceil(divideResult, bignumber("20"))
}

export const getRoundedValue = (value: BigNumber, unit: Unit): BigNumber => {
  const signMultiplier = value.isNegative() ? bignumber("-1") : bignumber("1")

  const y = apiMath.ceil(
    apiMath.abs(value).dividedBy(unit.roundingIncrement),
    20
  )

  const yDecimalPart = getParts(y)[1]

  let i: BigNumber

  if (yDecimalPart.equals(bignumber("0.5"))) {
    if (yDecimalPart.isNegative()) {
      i = floor(y, 0).add(bignumber("1"))
    } else {
      i = floor(y, 0)
    }
  } else {
    i = floor(y.add(bignumber("0.5")), 0)
  }

  return unit.roundingIncrement
    .times(i)
    .times(signMultiplier)
}

const getParts = (value: BigNumber): [BigNumber, BigNumber] => {
  const scaled = floor(value, 0)
  const decimal = subtract(value, scaled)
  return [scaled, decimal]
}

export const calculateTemperatureCorrection = (scaledTemperature: BigNumber, index = 0): BigNumber => {
  if (index === 7) {
    return multiply(A_COEFFICIENTS[index], scaledTemperature) as BigNumber
  } else {
    const coeff = A_COEFFICIENTS[index]
    return multiply(
      scaledTemperature,
      add(
        coeff,
        calculateTemperatureCorrection(scaledTemperature, index + 1)
      )
    ) as BigNumber
  }
}

const convertTemperatureToIpts68 = (temperature: Temperature): Temperature => {
  const temperatureInCelsius = temperature.to(TemperatureUnitCelsius)
  const scaledTemperature = divide(temperatureInCelsius.value, bignumber("630"))
  const temperatureCorrection = calculateTemperatureCorrection(scaledTemperature)
  const correctedTemperatureInCelsius = subtract(temperatureInCelsius.value, temperatureCorrection)
  const correctedTemperature = new Temperature(correctedTemperatureInCelsius, TemperatureUnitCelsius)
  return correctedTemperature.to(temperature.unit)
}

const calculateValueB = (density: BigNumber, commodity: Commodity): BigNumber => {
  const k0 = commodity.k0Coefficient
  const k1 = commodity.k1Coefficient
  const k2 = commodity.k2Coefficient

  const topValue = (k0.times(bignumber("2"))).add(k1.times(density))
  const bottomValue = k0.add((
    k1.add(k2.times(density))
      .times(density))
  )
  return divide(topValue, bottomValue)
}

const calculateValueA = (density: BigNumber, commodity: Commodity): BigNumber => {
  const k0 = commodity.k0Coefficient
  const k1 = commodity.k1Coefficient
  const k2 = commodity.k2Coefficient

  const multiplier = divide(bignumber("1"), density)

  const innerValue = divide(k0, density)
    .add(k1)
    .times(multiplier)
    .add(k2)

  return divide(TEMPERATURE_SHIFT_VALUE_FAHRENHEIT, bignumber("2")).times(innerValue)
}

const convertDensityAtBaseToIpts68 = (density: BigNumber, commodity: Commodity): BigNumber => {
  const valueA = calculateValueA(density, commodity)
  const valueB = calculateValueB(density, commodity)

  const nonExpInnerValue = bignumber("0.8")
    .times(valueA)
    .add(bignumber("1"))
    .times(valueA)

  const topInnerValue = exp(nonExpInnerValue)
    .minus(bignumber("1"))

  const bottomInnerValue = bignumber("1").add(
    valueA.times(
      bignumber("1").add(
        bignumber("1.6")
          .times(valueA)
      )
    ).times(valueB)
  )

  const innerValue = bignumber("1").add(divide(topInnerValue, bottomInnerValue))
  return density.times(innerValue)
}

const getThermalExpansionCoeffAtBase = (ipts68Density: BigNumber, commodity: Commodity): BigNumber => {
  const k0 = commodity.k0Coefficient
  const k1 = commodity.k1Coefficient
  const k2 = commodity.k2Coefficient

  const innerValue = add(divide(k0, ipts68Density), k1)

  const innerAddValue = divide(bignumber("1"), ipts68Density)

  const coeff = add(
    multiply(
      innerValue,
      innerAddValue
    ),
    k2
  )
  return coeff as BigNumber
}

const getTemperatureVolumeCorrectionFactor = (thermalExpansionCoeff: BigNumber, ipts68Temperature: BigNumber): BigNumber => {
  const tempDiff = subtract(ipts68Temperature, TEMPERATURE_BASE_FAHRENHEIT)

  const innerInnerValue =
    add(
      bignumber("1"),
      multiply(
        multiply(
          bignumber("0.8"),
          thermalExpansionCoeff
        ),
        add(tempDiff, TEMPERATURE_SHIFT_VALUE_FAHRENHEIT)
      )
    )

  const innerValue =
    multiply(
      bignumber(-thermalExpansionCoeff),
      multiply(
        tempDiff,
        innerInnerValue
      )
    ) as BigNumber

  return exp(innerValue) as BigNumber
}

const getScaledCompressibilityFactor = (ipts68Temperature: BigNumber, ipts68DensityAtBase: BigNumber): BigNumber => {
  const squaredIpts68Density = ipts68DensityAtBase.pow(bignumber("2"))

  const topRightInnerValue = bignumber("793920")
    .add(
      bignumber("2326.0")
        .times(ipts68Temperature)
    )

  const rightInnerValue = divide(topRightInnerValue, squaredIpts68Density)
  const middleInnerValue = bignumber("0.00013427").times(ipts68Temperature)
  const innerValue = bignumber("1.9947")
    .negated()
    .add(middleInnerValue)
    .add(rightInnerValue)

  return exp(innerValue)
}

const getPressureVolumeCorrectionFactor = (scaledCompressibility: BigNumber, alternatePressure: BigNumber): BigNumber => {
  const bottomValue = bignumber("1")
    .minus(
      bignumber("0.00001")
        .times(scaledCompressibility)
        .times(alternatePressure)
    )

  return divide(bignumber("1"), bottomValue)
}

const assertLimits = (
  commodityGroup: CommodityGroup,
  density: BigNumber,
  temperature: BigNumber,
  pressure: BigNumber
) => {

  const densityLimits = commodityGroup.kgm3Limits
  if (density.lessThan(densityLimits[0]) || density.greaterThan(densityLimits[1])) {
    throw new OutOfLimitsError(
      `The provided density (at base) value ${density} is outside of the accepted limits for commodity group ${commodityGroup.name}: ${densityLimits[0]} - ${densityLimits[1]}`
    )
  }

  if (temperature.lessThan(TEMPERATURE_FAHRENHEIT_LIMITS[0]) || temperature.greaterThan(TEMPERATURE_FAHRENHEIT_LIMITS[1])) {
    throw new OutOfLimitsError(`The provided alternate temperature value ${temperature} is outside of the accepted limits: ${TEMPERATURE_FAHRENHEIT_LIMITS[0]} - ${TEMPERATURE_FAHRENHEIT_LIMITS[1]}`)
  }

  if (pressure.lessThan(PRESSURE_PSIG_LIMITS[0]) || pressure.greaterThan(PRESSURE_PSIG_LIMITS[1])) {
    throw new OutOfLimitsError(`The provided alternate pressure value ${pressure} is outside of the accepted limits: ${PRESSURE_PSIG_LIMITS[0]} - ${PRESSURE_PSIG_LIMITS[1]}`)
  }

}

const getCombinedVolumeCorrectionFactor = (temperatureVolumeCorrection: BigNumber, pressureVolumeCorrection: BigNumber): BigNumber => {
  return temperatureVolumeCorrection.times(pressureVolumeCorrection)
}

export const getCorrectionFactors = (
  commodity: Commodity,
  density: BigNumber,
  altTemp: BigNumber,
  altPressure: BigNumber = bignumber("0")
): CorrectionFactors => {

  let pressure: BigNumber
  if (altPressure.lessThan(bignumber("0"))) {
    pressure = bignumber("0")
  } else {
    pressure = altPressure
  }

  assertLimits(
    commodity.group,
    density,
    altTemp,
    pressure
  )

  // Shift the temperature to IPTS-68 basis
  const ipts68Temperature =
    convertTemperatureToIpts68(
      new Temperature(altTemp, TemperatureUnitFahrenheit)
    ).value

  // Shift the density at base to IPTS-68 basis
  const ipts68DensityAtBase =
    convertDensityAtBaseToIpts68(
      density,
      commodity,
    )

  // Calculate the coefficient of thermal expansion at the base temperature of 60(F)
  const thermalExpansionCoeff =
    getThermalExpansionCoeffAtBase(
      ipts68DensityAtBase,
      commodity,
    )

  const temperatureVolumeCorrection =
    getTemperatureVolumeCorrectionFactor(
      thermalExpansionCoeff,
      ipts68Temperature
    )
  const scaledCompressibility =
    getScaledCompressibilityFactor(
      ipts68Temperature,
      ipts68DensityAtBase
    )
  const pressureVolumeCorrection =
    getPressureVolumeCorrectionFactor(
      scaledCompressibility,
      pressure
    )
  const combinedVolumeCorrection =
    getCombinedVolumeCorrectionFactor(
      temperatureVolumeCorrection,
      pressureVolumeCorrection
    )

  return {
    temperatureVolumeCorrection: temperatureVolumeCorrection,
    pressureVolumeCorrection: pressureVolumeCorrection,
    scaledCompressibility: scaledCompressibility,
    combinedVolumeCorrection: combinedVolumeCorrection,
    thermalExpansionCoeff: thermalExpansionCoeff
  }
}

export const getCorrectionFactorsForGroup = (
  commodityGroup: CommodityGroup,
  density: BigNumber,
  altTemp: BigNumber,
  altPressure: BigNumber = bignumber("0")
): CorrectionFactors => {
  const commodity = getCommodity(commodityGroup, density)
  return getCorrectionFactors(commodity, density, altTemp, altPressure)
}

const reviseDensityEstimate = (
  commodity: Commodity,
  obsDensity: BigNumber,
  estDensity: BigNumber,
  obsTemp: BigNumber,
  obsPressure: BigNumber,
  correctionFactors: CorrectionFactors
): BigNumber => {

  const calculateDensityCorrectionFactor = (): BigNumber => {
    const leftValue = divide(obsDensity, correctionFactors.combinedVolumeCorrection)
    return subtract(leftValue, estDensity)
  }

  const calculateTemperatureCorrectionFactor = (): BigNumber => {
    const dCoeff = commodity.dCoefficient
    const tempDiff = subtract(obsTemp, bignumber("60"))
    const thermalExpansionCoeff = correctionFactors.thermalExpansionCoeff

    const innerValue = add(
      bignumber("1"),
      multiply(
        multiply(
          bignumber("1.6"),
          thermalExpansionCoeff
        ),
        tempDiff
      )
    )

    const value = multiply(
      multiply(
        multiply(
          dCoeff,
          thermalExpansionCoeff
        ),
        tempDiff,
      ),
      innerValue
    )

    return value as BigNumber
  }

  const calculatePressureCorrectionFactor = (): BigNumber => {
    const pressureVolumeCorrection = correctionFactors.pressureVolumeCorrection
    const scaledCompressibility = correctionFactors.scaledCompressibility

    const topInnerValue = add(
      bignumber("7.93920"),
      multiply(
        bignumber("0.02326"),
        obsTemp
      )
    )

    const topValue =
      multiply(
        bignumber("2"),
        multiply(
          pressureVolumeCorrection,
          multiply(
            obsPressure,
            multiply(
              scaledCompressibility,
              topInnerValue
            )
          )
        )
      ) as BigNumber

    return divide(topValue, estDensity.pow(bignumber("2"))).negated()
  }

  const calculateChange = (): BigNumber => {
    const densityCorrectionFactor = calculateDensityCorrectionFactor()
    const temperatureCorrectionFactor = calculateTemperatureCorrectionFactor()
    const pressureCorrectionFactor = calculatePressureCorrectionFactor()

    const bottomValue =
      add(
        bignumber("1"),
        add(
          temperatureCorrectionFactor,
          pressureCorrectionFactor,
        )
      )

    return divide(densityCorrectionFactor, bottomValue)
  }

  const change = calculateChange()
  let newEstimatedDensity = add(estDensity, change)
  const commodityLimits = commodity.group.kgm3At60Limits

  while (newEstimatedDensity.lessThan(commodityLimits[0]) || newEstimatedDensity.greaterThan(commodityLimits[1])) {
    let changeValue: BigNumber
    if (newEstimatedDensity.lessThan(commodityLimits[0])) {
      changeValue = subtract(commodityLimits[0], estDensity)
    } else {
      changeValue = subtract(commodityLimits[1], estDensity)
    }

    newEstimatedDensity = add(estDensity, changeValue)
  }

  return newEstimatedDensity
}

const isEstimateSatisfactory = (
  obsDensity: BigNumber,
  estDensity: BigNumber,
  combinedVolumeCorrection: BigNumber
): boolean => {
  const diff =
    subtract(
      obsDensity,
      multiply(
        estDensity,
        combinedVolumeCorrection
      )
    ) as BigNumber

  return diff.abs().lessThan(bignumber(0.000001))
}

export const getDensityAndVolumeAtBase = (
  commodityGroup: CommodityGroup,
  obsDensity: Density,
  obsTemp: Temperature,
  obsPressure: Pressure = new Pressure(bignumber("0"), PressureUnitPsig),
  obsVolume: number | BigNumber | undefined = undefined
): [Density, BigNumber?] => {

  // convert values to required units
  const obsDensityValue = obsDensity.to(DensityUnitKgm3).value
  const obsTempValue = obsTemp.to(TemperatureUnitFahrenheit).value
  let obsPressureValue = obsPressure.to(PressureUnitPsig).value

  // correct pressure
  const minPressureValue = bignumber("0")
  if (obsPressureValue.lessThan(minPressureValue)) {
    obsPressureValue = minPressureValue
  }

  // check limits
  assertLimits(
    commodityGroup,
    obsDensityValue,
    obsTempValue,
    obsPressureValue
  )

  // Iterate up to fifteen times until we find a satisfactory estimated density at base.
  // If we can not find a satisfactory estimated density, then we have a non-convergence case.

  const densityAtBaseLimits = commodityGroup.kgm3At60Limits
  let estimatedDensityAtBase = obsDensityValue
  if (estimatedDensityAtBase.lessThan(densityAtBaseLimits[0])) {
    estimatedDensityAtBase = densityAtBaseLimits[0]
  } else if (estimatedDensityAtBase.greaterThan(densityAtBaseLimits[1])) {
    estimatedDensityAtBase = densityAtBaseLimits[1]
  }

  let commodity = getCommodity(commodityGroup, estimatedDensityAtBase)

  let correctionFactors = getCorrectionFactors(
    commodity,
    estimatedDensityAtBase,
    obsTempValue,
    obsPressureValue
  )

  let iteration = 0

  while (!isEstimateSatisfactory(obsDensityValue, estimatedDensityAtBase, correctionFactors.combinedVolumeCorrection)) {
    if (iteration > 15) {
      throw new NonConvergenceError("Could not retrieve corrected density at base")
    }

    estimatedDensityAtBase =
      reviseDensityEstimate(
        commodity,
        obsDensityValue,
        estimatedDensityAtBase,
        obsTempValue,
        obsPressureValue,
        correctionFactors
      )

    commodity = getCommodity(commodityGroup, estimatedDensityAtBase)

    correctionFactors = getCorrectionFactors(
      commodity,
      estimatedDensityAtBase,
      obsTempValue,
      obsPressureValue
    )

    iteration = iteration + 1
  }

  assertLimits(
    commodityGroup,
    estimatedDensityAtBase,
    obsTempValue,
    obsPressureValue
  )

  let volumeAtBase: BigNumber | undefined = undefined

  if (typeof obsVolume !== "undefined") {
    volumeAtBase = bignumber(obsVolume).times(correctionFactors.combinedVolumeCorrection)
  }

  const estimatedDensity = new Density(estimatedDensityAtBase, DensityUnitKgm3)

  return [estimatedDensity, volumeAtBase]
}
